// The rail (R-U7, R-U28, um/2026-10-29/01 п.7, um/2026-11-02/04 п.2): icons of the sections only, the name in a tooltip; the search (⌘K)
// first, in a top zone of the top line's height and on its line; the state of the sync, the theme, folding the column; the avatar menu
// leads to «Настройки».

import { avatarUrl } from '@confdeck/core-shared';
import type { Ref } from 'react';
import type { MenuSection } from '@confdeck/core-shared';
import { useAccess, useMeta, useStatus, useSync } from '../sync/react.tsx';
import { Icon } from '../ui/icons.tsx';
import { Tooltip } from '../ui/Tooltip.tsx';
import { IconButton, Avatar, Button, Menu, MenuContent, MenuItem, MenuLabel, MenuSeparator, MenuTrigger, cx, isMac } from '../ui/primitives.tsx';
import { navigate } from './router.ts';
import { useVariant } from './variants.tsx';
import { VariantsButton } from './VariantBar.tsx';
import s from './shell.module.css';

export function Rail({ sections, section, here, onOpen, onLogout, onSearch, sidebar, peek, navRef }: {
  sections: MenuSection[]; section: MenuSection | undefined; here: 'automations' | 'settings' | null;
  onOpen: (m: MenuSection) => void; onLogout: () => void; onSearch: () => void; sidebar: { label: string; toggle: () => void };
  /** A collapsed second menu comes out over the content while the pointer is on a section button (R-U7). */
  peek?: { show: () => void; leave: (to: EventTarget | null) => void };
  navRef?: Ref<HTMLElement>;
}) {
  const access = useAccess();
  return (
    <nav ref={navRef} className={s.rail} aria-label="Разделы" onMouseLeave={(e) => peek?.leave(e.relatedTarget)}>
      {/* No logo (um/2026-11-02/04 п.2): the search stands in the middle of a zone as high as the top line, under the same line. */}
      <div className={s.railTop}>
        <Tooltip label={`Поиск (${isMac ? '⌘' : 'Ctrl'}+K)`}>
          <button type="button" className={s.railBtn} aria-label="Поиск" data-rail-search onClick={onSearch}><Icon name="search" size={18} /></button>
        </Tooltip>
      </div>
      {sections.map((m) => (
        <Tooltip key={m.key} label={m.label}>
          <button type="button" className={cx(s.railBtn, !here && m === section && s.railOn)} aria-label={m.label} aria-current={!here && m === section ? 'page' : undefined} onClick={() => onOpen(m)} onMouseEnter={peek?.show}>
            <Icon name={m.icon} size={18} />
          </button>
        </Tooltip>
      ))}
      <span className={s.railSpacer} />
      {/* Scenarios are the account's settings: a person with limited roles (a volunteer) does not see them (Grok audit, round 2). */}
      {access.all && (
        <Tooltip label="Автоматизации">
          <button type="button" className={cx(s.railBtn, here === 'automations' && s.railOn)} aria-label="Автоматизации" onClick={() => navigate('/a')}>
            <Icon name="zap" size={18} />
          </button>
        </Tooltip>
      )}
      <span className={s.railSep} />
      <VariantsButton />
      <SyncState />
      <ThemeButton />
      {/* Every section has the column, so folding it is always here — the mail too (um/2026-10-30/03 п.1). */}
      <Tooltip label={`${sidebar.label} (${isMac ? '⌘' : 'Ctrl'}+\\)`}>
        <IconButton icon="sidebar" label={sidebar.label} onClick={sidebar.toggle} className={s.railTool} />
      </Tooltip>
      <UserMenu onLogout={onLogout} on={here === 'settings'} />
    </nav>
  );
}

/**
 * The state of the sync (was «Всё сохранено» of the top bar): a quiet dot while all is saved; edits waiting or no connection show a
 * coloured dot with the count, and the words in the tooltip and to a screen reader.
 */
function SyncState() {
  const { state, unsent } = useStatus();
  const label = state === 'online' ? (unsent ? `Сохраняется: ${unsent}` : 'Всё сохранено') : state === 'connecting' ? 'Подключение…' : unsent ? `Нет сети, ждут отправки: ${unsent}` : 'Нет сети, работаем по копии';
  return (
    <Tooltip label={label}>
      <span className={cx(s.sync, s[`st_${state}`], !!unsent && s.syncWaiting)} role="status" aria-live="polite" aria-label={label} tabIndex={0}>
        <i />{unsent > 0 && <b>{unsent > 99 ? '99+' : unsent}</b>}
      </span>
    </Tooltip>
  );
}

function ThemeButton() {
  const [theme, setTheme] = useVariant('theme');
  const resolved = document.documentElement.dataset.theme;
  const next = theme === 'system' ? (resolved === 'dark' ? 'light' : 'dark') : theme === 'dark' ? 'light' : 'dark';
  return (
    <Tooltip label={next === 'dark' ? 'Тёмная тема' : 'Светлая тема'}>
      <IconButton icon={resolved === 'dark' ? 'sun' : 'moon'} label="Сменить тему" className={s.railTool} onClick={() => setTheme(next)} />
    </Tooltip>
  );
}

/** The person's menu: who is signed in, «Настройки» (one section for the account and oneself), reload, sign out. */
function UserMenu({ onLogout, on }: { onLogout: () => void; on: boolean }) {
  const meta = useMeta();
  const client = useSync();
  const me = meta.user(client.header?.userId);
  return (
    <Menu>
      <MenuTrigger asChild><button type="button" className={cx(s.me, on && s.meOn)} aria-label="Профиль">{me && <Avatar name={me.name} color={me.color} src={avatarUrl(me)} size={26} />}</button></MenuTrigger>
      <MenuContent side="right" align="end">
        <MenuLabel>{client.header?.workspace.name} · {me?.name} · {me?.email}</MenuLabel>
        <MenuSeparator />
        <MenuItem icon="gear" onSelect={() => navigate('/settings')}>Настройки</MenuItem>
        <MenuItem icon="undo" onSelect={async () => {
          if (client.unsent && !confirm(`Не отправлено правок: ${client.unsent}. Перезагрузить данные и потерять их?`)) return;
          await client.resetCache(); location.reload();
        }}>Перезагрузить данные</MenuItem>
        <MenuItem icon="logout" onSelect={onLogout}>Выйти</MenuItem>
      </MenuContent>
    </Menu>
  );
}
